import React, { useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Check, Loader2, ChevronRight, ChevronLeft, ShieldCheck, Home, Download } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useToast } from "../hooks/use-toast";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEL = "h-10 w-full rounded-md border border-[#e5ddd0] bg-white px-3 text-sm text-[#16323f] focus:border-[#35a4a4] focus:outline-none";

const STEPS = [
  "The Property",
  "A Little About You",
  "Employment & Income",
  "Other occupants",
  "References",
  "Declarations & Sign-off",
  "Review & submit",
];

const RTR = ["British / Irish citizen", "Settled / pre-settled status", "Valid visa with right to rent", "Share code available", "Not sure — need to check"];
const EMPLOYMENT = ["Employed – full time", "Employed – part time", "Self-employed", "Student", "Retired", "Not currently working", "Other"];
const LIVING = ["Renting privately (direct from landlord)", "Renting through an agent", "Living with family / friends", "Homeowner", "Other"];

const DECLARATIONS = [
  ["decl_truthful", "I understand that providing false or misleading information may result in my application being withdrawn or any holding deposit being dealt with in accordance with the Tenant Fees Act 2019."],
  ["decl_consent", "I consent to referencing being carried out by New Space and its independent third-party referencing provider on behalf of everyone named in this application, including contacting our employers, current or previous landlords/agents, and any guarantor."],
  ["decl_holding_deposit", "I understand that if the offer is accepted, the third-party referencing provider will be in touch to complete referencing, and each applicant will be asked to pay a holding deposit (up to one week's rent) to secure the property, handled in line with the Tenant Fees Act 2019."],
  ["decl_right_to_rent", "I understand that everyone moving in must provide original identification documents to complete a Right to Rent check before any tenancy can commence."],
  ["decl_no_guarantee", "I acknowledge that submitting this offer does not guarantee that a tenancy will be offered until referencing is complete and the landlord has formally accepted the application."],
];

function YesNo({ value, onChange, name, options = ["No", "Yes"] }) {
  return (
    <div className="flex gap-2">
      {options.map((o) => (
        <button key={o} type="button" data-testid={`offer-${name}-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
          onClick={() => onChange(o)}
          className={`flex-1 rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
            value === o ? "border-[#35a4a4] bg-[#35a4a4] text-white" : "border-[#e5ddd0] bg-white text-[#5b5450] hover:border-[#35a4a4]"
          }`}>
          {o}
        </button>
      ))}
    </div>
  );
}

const Field = ({ label, hint, children, required }) => (
  <div className="space-y-1.5">
    <Label>{label}{required && " *"}</Label>
    {children}
    {hint && <p className="text-xs text-[#7a716a]">{hint}</p>}
  </div>
);

function Content() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [savedId, setSavedId] = useState("");

  const [form, setForm] = useState({
    property_address: params.get("property") || "",
    offer_rent: params.get("rent") || "",
    property_uuid: params.get("pid") || "",
    property_title: params.get("property") || "",
    move_in_date: "", adult_occupants: "1", co_applicants: [], smokers: "No", children: "No", pets: "No",
    full_name: "", date_of_birth: "", current_address: "", time_at_address: "", nationality: "",
    phone: "", email: "", right_to_rent: "", other_adults: "No, just me",
    employment_status: "", employer_name: "", job_title: "", length_in_role: "", annual_income: "", guarantor: "No",
    living_situation: "", landlord_name: "", landlord_phone: "", landlord_email: "",
    reason_for_leaving: "", employer_ref_name: "", employer_ref_email: "",
    notes: "",
    decl_truthful: false, decl_consent: false, decl_holding_deposit: false, decl_right_to_rent: false, decl_no_guarantee: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const setOccupants = (val) => {
    const n = Math.max(1, parseInt(val || "1", 10) || 1);
    setForm((f) => {
      const extra = n - 1;
      const co = [...(f.co_applicants || [])];
      while (co.length < extra) co.push({ name: "", email: "", phone: "", employment_status: "", employer: "", annual_income: "" });
      co.length = extra;
      return { ...f, adult_occupants: String(n), co_applicants: co };
    });
  };
  const setCo = (i, k, v) => setForm((f) => {
    const co = [...(f.co_applicants || [])];
    co[i] = { ...co[i], [k]: v };
    return { ...f, co_applicants: co };
  });

  const stepValid = useMemo(() => {
    if (step === 1) return form.property_address && form.offer_rent && form.move_in_date;
    if (step === 2) return form.full_name && form.date_of_birth && form.current_address && form.phone && form.email;
    if (step === 3) return !!form.annual_income;
    if (step === 4) return (form.co_applicants || []).every((c) => c.name && c.name.trim());
    if (step === 5) return !!form.reason_for_leaving;
    if (step === 6) return DECLARATIONS.every(([k]) => form[k]);
    return true;
  }, [step, form]);

  const next = () => {
    if (!stepValid) {
      toast({ title: "A few details still need attention", description: "Please complete the required fields on this step to continue.", variant: "destructive" });
      return;
    }
    setStep((s) => Math.min(7, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => { setStep((s) => Math.max(1, s - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/offers`, form);
      setSavedId(res.data?.id || "");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const startOffer = () => {
    setStep(1);
    document.getElementById("offer-form")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="App">
      <Seo title="Make an Offer | New Space Lettings, Hackney" description="Make an offer on your next home with New Space. Complete our quick tenant offer form and it goes straight to our lettings team." path="/make-an-offer" />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-16 lg:pt-40">
        <div className="mx-auto max-w-[900px] px-5 text-center">
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#d8b074]">Independent lettings &amp; management — Hackney &amp; North London</p>
          <h1 className="mt-4 font-display text-4xl font-700 uppercase tracking-wide text-white lg:text-6xl">
            Make an offer<br />on your next home.
          </h1>
          <p className="mx-auto mt-4 max-w-lg text-white/80">
            Complete the form below and your offer goes straight to our lettings team. You'll receive a copy by email.
          </p>
          {!done && (
            <button onClick={startOffer} data-testid="start-offer-button"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#ec008c] px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]">
              Start your offer <ChevronRight size={16} />
            </button>
          )}
        </div>
      </section>

      <section className="bg-[#faf7f0] py-14 lg:py-20" id="offer-form">
        <div className="mx-auto max-w-[1040px] px-5">
          {done ? (
            <div className="mx-auto max-w-xl rounded-2xl border border-[#e5ddd0] bg-white p-10 text-center shadow-lg" data-testid="offer-success">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#35a4a4] text-white"><Check size={34} /></div>
              <h2 className="mt-5 font-display text-2xl font-600 text-[#16323f]">Offer submitted</h2>
              <p className="mx-auto mt-3 max-w-md text-sm text-[#5b5450]">
                Thank you — your offer on <strong>{form.property_address}</strong> has been sent straight to our lettings team,
                and a copy is on its way to your email. We'll review it and be in touch to discuss the next steps.
              </p>
              <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                {savedId && (
                  <a href={`${API}/offers/${savedId}/pdf`} target="_blank" rel="noopener noreferrer"
                    data-testid="download-offer-pdf-button"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#35a4a4] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#2b8686]">
                    <Download size={16} /> Download offer (PDF)
                  </a>
                )}
                <button onClick={() => navigate("/")} data-testid="offer-home-button"
                  className="rounded-full bg-[#16323f] px-9 py-3 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#ec008c]">
                  Back to main site
                </button>
              </div>
            </div>
          ) : (
            <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
              {/* Stepper */}
              <aside className="lg:sticky lg:top-28 lg:self-start">
                <div className="rounded-2xl border border-[#e5ddd0] bg-white p-6 shadow-sm">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">Your offer</p>
                  <ol className="mt-4 space-y-1">
                    {STEPS.map((label, i) => {
                      const n = i + 1;
                      const active = n === step, doneStep = n < step;
                      return (
                        <li key={label} className={`flex items-center gap-3 rounded-lg px-2 py-2 ${active ? "bg-[#16323f]/5" : ""}`}>
                          <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-700 ${
                            active ? "bg-[#ec008c] text-white" : doneStep ? "bg-[#35a4a4] text-white" : "bg-[#eee6da] text-[#7a716a]"
                          }`}>
                            {doneStep ? <Check size={14} strokeWidth={3} /> : String(n).padStart(2, "0")}
                          </span>
                          <span className={`text-sm ${active ? "font-600 text-[#16323f]" : "text-[#7a716a]"}`}>{label}</span>
                        </li>
                      );
                    })}
                  </ol>
                </div>
              </aside>

              {/* Form card */}
              <div className="rounded-2xl border border-[#e5ddd0] bg-white p-7 shadow-sm lg:p-9">
                <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">Step {step} of {STEPS.length}</p>
                <h2 className="mt-1 font-display text-2xl font-600 uppercase tracking-wide text-[#16323f]">{STEPS[step - 1]}</h2>
                <span className="mt-3 block h-[3px] w-14 rounded-full bg-[#eaa32a]" />

                {step === 1 && (
                  <div className="mt-6 space-y-5">
                    <Field label="Property address" required>
                      <Input value={form.property_address} onChange={(e) => set("property_address", e.target.value)} placeholder="Tell us which property you're applying for." data-testid="offer-property-address" />
                    </Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Monthly rent offered (pcm)" required>
                        <Input value={form.offer_rent} onChange={(e) => set("offer_rent", e.target.value)} placeholder="What do you wish to offer per month?" data-testid="offer-rent" />
                      </Field>
                      <Field label="Preferred move-in date" required>
                        <Input type="date" value={form.move_in_date} onChange={(e) => set("move_in_date", e.target.value)} data-testid="offer-move-in" />
                      </Field>
                    </div>
                    <Field label="How many people (adults) will be moving in?" hint="Include yourself. We'll ask for each additional adult's details next.">
                      <select className={SEL} value={form.adult_occupants || "1"} onChange={(e) => setOccupants(e.target.value)} data-testid="offer-occupants">
                        {["1", "2", "3", "4", "5", "6"].map((n) => <option key={n} value={n}>{n === "6" ? "6+" : n}</option>)}
                      </select>
                    </Field>
                    <div className="grid gap-5 sm:grid-cols-3">
                      <Field label="Does anyone smoke?" hint="So we can check against the property's terms.">
                        <YesNo name="smokers" value={form.smokers} onChange={(v) => set("smokers", v)} />
                      </Field>
                      <Field label="Any children moving in?" hint="Helps us match the property to your household.">
                        <YesNo name="children" value={form.children} onChange={(v) => set("children", v)} />
                      </Field>
                      <Field label="Any pets?" hint="Some properties have pet restrictions — we'll check for you.">
                        <YesNo name="pets" value={form.pets} onChange={(v) => set("pets", v)} />
                      </Field>
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="mt-6 space-y-5">
                    <p className="text-sm text-[#7a716a]">Your identity, contact details and Right to Rent status.</p>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Full legal name" required>
                        <Input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} placeholder="Enter your full legal name." data-testid="offer-full-name" />
                      </Field>
                      <Field label="Date of birth" required>
                        <Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} data-testid="offer-dob" />
                      </Field>
                    </div>
                    <Field label="Current address" required>
                      <Textarea rows={2} value={form.current_address} onChange={(e) => set("current_address", e.target.value)} placeholder="Enter your current address." data-testid="offer-current-address" />
                    </Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Time at current address">
                        <Input value={form.time_at_address} onChange={(e) => set("time_at_address", e.target.value)} placeholder="e.g. 2 years 3 months" />
                      </Field>
                      <Field label="Nationality">
                        <Input value={form.nationality} onChange={(e) => set("nationality", e.target.value)} placeholder="e.g. British" />
                      </Field>
                      <Field label="Phone number" required>
                        <Input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Enter a phone number." data-testid="offer-phone" />
                      </Field>
                      <Field label="Email address" required>
                        <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="Enter a valid email address." data-testid="offer-email" />
                      </Field>
                    </div>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Right to rent in the UK">
                        <select className={SEL} value={form.right_to_rent} onChange={(e) => set("right_to_rent", e.target.value)} data-testid="offer-right-to-rent">
                          <option value="">Select…</option>
                          {RTR.map((o) => <option key={o} value={o}>{o}</option>)}
                        </select>
                      </Field>
                      <Field label="Are other adults moving in with you?">
                        <YesNo name="other-adults" options={["No, just me", "Yes"]} value={form.other_adults} onChange={(v) => set("other_adults", v)} />
                      </Field>
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="mt-6 space-y-5">
                    <p className="text-sm text-[#7a716a]">Help us confirm affordability and reference your application.</p>
                    <Field label="Employment status">
                      <select className={SEL} value={form.employment_status} onChange={(e) => set("employment_status", e.target.value)} data-testid="offer-employment-status">
                        <option value="">Select…</option>
                        {EMPLOYMENT.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Employer / business name">
                        <Input value={form.employer_name} onChange={(e) => set("employer_name", e.target.value)} placeholder="e.g. New Space London Ltd" />
                      </Field>
                      <Field label="Job title">
                        <Input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} placeholder="e.g. Product designer" />
                      </Field>
                      <Field label="Length in current role">
                        <Input value={form.length_in_role} onChange={(e) => set("length_in_role", e.target.value)} placeholder="e.g. 3 years" />
                      </Field>
                      <Field label="Annual gross income (£)" required>
                        <Input value={form.annual_income} onChange={(e) => set("annual_income", e.target.value)} placeholder="Enter your annual gross income." data-testid="offer-income" />
                      </Field>
                    </div>
                    <Field label="Will you be offering a guarantor?">
                      <YesNo name="guarantor" value={form.guarantor} onChange={(v) => set("guarantor", v)} />
                    </Field>
                  </div>
                )}

                {step === 4 && (
                  <div className="mt-6 space-y-5">
                    {(form.co_applicants || []).length === 0 ? (
                      <div className="rounded-xl border border-[#eee6da] bg-[#faf7f0] p-6 text-center" data-testid="offer-no-co-applicants">
                        <p className="text-sm text-[#5b5450]">You've told us it's just you moving in, so there's nothing to add here — tap Continue.</p>
                        <button type="button" onClick={() => setStep(1)} className="mt-3 text-xs font-semibold uppercase tracking-[0.2em] text-[#ec008c]">Change number of people</button>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-[#7a716a]">Please add the other adults who'll be living at the property — their contact details, work details and landlord reference so we can reference the whole household.</p>
                        {(form.co_applicants || []).map((c, i) => (
                          <div key={i} className="rounded-xl border border-[#e5ddd0] p-5" data-testid={`offer-co-block-${i}`}>
                            <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.3em] text-[#c9a25e]">Person {i + 2}</p>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <Field label="Full name" required>
                                <Input value={c.name} onChange={(e) => setCo(i, "name", e.target.value)} placeholder="Full legal name" data-testid={`offer-co-${i}-name`} />
                              </Field>
                              <Field label="Email">
                                <Input type="email" value={c.email} onChange={(e) => setCo(i, "email", e.target.value)} placeholder="Email address" data-testid={`offer-co-${i}-email`} />
                              </Field>
                              <Field label="Phone">
                                <Input type="tel" value={c.phone} onChange={(e) => setCo(i, "phone", e.target.value)} placeholder="Contact number" data-testid={`offer-co-${i}-phone`} />
                              </Field>
                              <Field label="Employment status">
                                <select className={SEL} value={c.employment_status} onChange={(e) => setCo(i, "employment_status", e.target.value)} data-testid={`offer-co-${i}-employment`}>
                                  <option value="">Select…</option>
                                  {EMPLOYMENT.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </Field>
                              <Field label="Employer / job title">
                                <Input value={c.employer} onChange={(e) => setCo(i, "employer", e.target.value)} placeholder="e.g. Acme Ltd — Designer" data-testid={`offer-co-${i}-employer`} />
                              </Field>
                              <Field label="Annual gross income (£)">
                                <Input value={c.annual_income} onChange={(e) => setCo(i, "annual_income", e.target.value)} placeholder="e.g. 42000" data-testid={`offer-co-${i}-income`} />
                              </Field>
                            </div>
                            <p className="mt-5 mb-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#7a716a]">Landlord reference</p>
                            <div className="grid gap-4 sm:grid-cols-2">
                              <Field label="Current living situation">
                                <select className={SEL} value={c.living_situation || ""} onChange={(e) => setCo(i, "living_situation", e.target.value)} data-testid={`offer-co-${i}-living`}>
                                  <option value="">Select…</option>
                                  {LIVING.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                              </Field>
                              <Field label="Current landlord / agent name">
                                <Input value={c.landlord_name || ""} onChange={(e) => setCo(i, "landlord_name", e.target.value)} placeholder="For their landlord reference" data-testid={`offer-co-${i}-landlord-name`} />
                              </Field>
                              <Field label="Landlord / agent phone">
                                <Input type="tel" value={c.landlord_phone || ""} onChange={(e) => setCo(i, "landlord_phone", e.target.value)} placeholder="Contact number" data-testid={`offer-co-${i}-landlord-phone`} />
                              </Field>
                              <Field label="Landlord / agent email">
                                <Input type="email" value={c.landlord_email || ""} onChange={(e) => setCo(i, "landlord_email", e.target.value)} placeholder="Email address" data-testid={`offer-co-${i}-landlord-email`} />
                              </Field>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}

                {step === 5 && (
                  <div className="mt-6 space-y-5">
                    <p className="text-sm text-[#7a716a]">Landlord and employer contacts we may speak to.</p>
                    <Field label="Current living situation">
                      <select className={SEL} value={form.living_situation} onChange={(e) => set("living_situation", e.target.value)} data-testid="offer-living-situation">
                        <option value="">Select…</option>
                        {LIVING.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Current landlord / agent name">
                        <Input value={form.landlord_name} onChange={(e) => set("landlord_name", e.target.value)} placeholder="Enter their name." />
                      </Field>
                      <Field label="Their phone number">
                        <Input type="tel" value={form.landlord_phone} onChange={(e) => set("landlord_phone", e.target.value)} placeholder="Enter a contact number." />
                      </Field>
                    </div>
                    <Field label="Their email address">
                      <Input type="email" value={form.landlord_email} onChange={(e) => set("landlord_email", e.target.value)} placeholder="Enter a valid email address." />
                    </Field>
                    <Field label="Reason for leaving your current address" required>
                      <Textarea rows={2} value={form.reason_for_leaving} onChange={(e) => set("reason_for_leaving", e.target.value)} placeholder="Let us know why you're leaving." data-testid="offer-reason" />
                    </Field>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <Field label="Employer reference — contact name">
                        <Input value={form.employer_ref_name} onChange={(e) => set("employer_ref_name", e.target.value)} placeholder="e.g. Your line manager" />
                      </Field>
                      <Field label="Employer reference — email">
                        <Input type="email" value={form.employer_ref_email} onChange={(e) => set("employer_ref_email", e.target.value)} placeholder="Enter a valid email address." />
                      </Field>
                    </div>
                  </div>
                )}

                {step === 6 && (
                  <div className="mt-6 space-y-5">
                    <p className="text-sm text-[#7a716a]">Almost done — the legal bits.</p>
                    <div className="flex items-start gap-3 rounded-xl border border-[#35a4a4]/30 bg-[#35a4a4]/5 p-4" data-testid="offer-process-note">
                      <ShieldCheck size={18} className="mt-0.5 shrink-0 text-[#35a4a4]" />
                      <p className="text-sm leading-relaxed text-[#16323f]">
                        <strong>How referencing works:</strong> if your offer is accepted, our independent third-party referencing provider will be in touch with everyone named to complete referencing. Once that's underway, each applicant pays a <strong>holding deposit (up to one week's rent)</strong> to secure the property, in line with the Tenant Fees Act 2019.
                      </p>
                    </div>
                    <Field label="Any requests or anything else that would be helpful to know to get the property agreed?">
                      <Textarea rows={3} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Anything at all — dates, furniture, flexibility." />
                    </Field>
                    <div className="space-y-3">
                      {DECLARATIONS.map(([key, text]) => (
                        <label key={key} htmlFor={`decl-${key}`}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${form[key] ? "border-[#35a4a4] bg-[#35a4a4]/5" : "border-[#e5ddd0]"}`}>
                          <Checkbox id={`decl-${key}`} data-testid={`offer-${key.replace(/_/g, "-")}`} checked={form[key]} onCheckedChange={(v) => set(key, !!v)} className="mt-0.5" />
                          <span className="text-sm leading-relaxed text-[#5b5450]">{text}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                {step === 7 && (
                  <div className="mt-6 space-y-4">
                    <p className="text-sm text-[#7a716a]">Please review your offer before submitting.</p>
                    <div className="overflow-hidden rounded-xl border border-[#eee6da]">
                      {[
                        ["Property", form.property_address],
                        ["Offer (pcm)", form.offer_rent],
                        ["Preferred move-in", form.move_in_date],
                        ["Adult occupants", form.adult_occupants],
                        ["Smokers / children / pets", `${form.smokers} / ${form.children} / ${form.pets}`],
                        ["Name", form.full_name],
                        ["Phone", form.phone],
                        ["Email", form.email],
                        ["Right to rent", form.right_to_rent],
                        ["Employment", form.employment_status],
                        ["Annual income", form.annual_income],
                        ["Guarantor", form.guarantor],
                        ["Current living situation", form.living_situation],
                        ["Reason for leaving", form.reason_for_leaving],
                        ["Other occupants", (form.co_applicants || []).map((c, i) => `Person ${i + 2}: ${c.name || "—"}${c.annual_income ? ` (£${c.annual_income})` : ""}`).join("; ")],
                        ["Notes", form.notes],
                      ].filter(([, v]) => v).map(([k, v], i) => (
                        <div key={k} className={`grid grid-cols-[40%_60%] gap-3 px-4 py-2.5 text-sm ${i % 2 ? "bg-[#faf7f0]" : "bg-white"}`}>
                          <span className="text-[#7a716a]">{k}</span>
                          <span className="font-medium text-[#16323f]">{v}</span>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-[#7a716a]">By submitting, your offer is sent to our lettings team and a copy is emailed to you. Submitting doesn't guarantee a tenancy until referencing is complete and the landlord has formally accepted.</p>
                  </div>
                )}

                {/* Nav */}
                <div className="mt-8 flex items-center justify-between border-t border-[#eee6da] pt-6">
                  <button type="button" onClick={back} disabled={step === 1}
                    data-testid="offer-back-button"
                    className="inline-flex items-center gap-2 rounded-full border border-[#e5ddd0] px-6 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#5b5450] transition-colors hover:border-[#16323f] disabled:opacity-40">
                    <ChevronLeft size={16} /> Back
                  </button>
                  {step < 7 ? (
                    <button type="button" onClick={next} data-testid="offer-continue-button"
                      className="inline-flex items-center gap-2 rounded-full bg-[#eaa32a] px-8 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#d8901a]">
                      Continue <ChevronRight size={16} />
                    </button>
                  ) : (
                    <button type="button" onClick={submit} disabled={loading} data-testid="offer-submit-button"
                      className="inline-flex items-center gap-2 rounded-full bg-[#ec008c] px-8 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074] disabled:opacity-70">
                      {loading ? (<><Loader2 size={16} className="animate-spin" /> Submitting…</>) : (<><ShieldCheck size={16} /> Submit my offer</>)}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function MakeAnOffer() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
